'use client'

import { useState } from 'react'
import { Head, router } from '@inertiajs/react'
import { LinkIcon, CheckIcon } from '@heroicons/react/24/outline'
import { Radio, RadioGroup } from '@headlessui/react'
import Theme from "@/Pages/Public/Components/Theme.jsx"
import ReviewForm from "@/Pages/Public/Components/ReviewForm.jsx"
import ReviewList from "@/Pages/Public/Components/ReviewList.jsx"
import { ShoppingBagIcon } from '@heroicons/react/24/outline'

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

export default function ProductDetail({
    auth,
    pageTitle = "",
    relatedProducts = [],
    product,
    productS = {},
    reviews = [],
    policies = [],
    siteName = "",
    canonicalUrl = "",
    seoDescription = "",
    productSchemaJson = ""
}) {
    const colors = product.colors || []
    const sizes = product.sizes || []
    const variations = product.variations || []
    const [copied, setCopied] = useState(false)
    const copyLink = () => {
        navigator.clipboard.writeText(window.location.href).then(() => {
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        })
    }
    const [selectedColor, setSelectedColor] = useState(null)
    const [selectedSize, setSelectedSize] = useState(null)
    const [quantity, setQuantity] = useState(1)
    const [activeImage, setActiveImage] = useState(0)
    const [message, setMessage] = useState(null)
    const selectedVariation = variations.find((variation) =>
        (!colors.length || variation.color_id === selectedColor?.id) &&
        (!sizes.length || variation.size_id === selectedSize?.id)
    )
    const selectionReady = (!colors.length || selectedColor) && (!sizes.length || selectedSize)
    const availableStock = selectedVariation ? selectedVariation.stock : (variations.length ? 0 : product.quantity)

    const imagesToShow = (() => {
        if (product.media_urls && product.media_urls.length > 0) {
            return product.media_urls.map((url, index) => ({
                id: index,
                imageSrc: url,
                imageAlt: `${product.name} image ${index + 1}`,
            }));
        }
        if (productS.images && productS.images.length > 0 && productS.images[0].src !== '/default.png') {
            return productS.images.map((img) => ({
                id: img.id,
                imageSrc: img.src,
                imageAlt: img.alt,
            }));
        }
        return [];
    })()

    return (
        <Theme>
            <Head>
                <title>{pageTitle}</title>
                <meta property="og:type" content="website" />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={seoDescription} />
                <meta property="og:site_name" content={siteName} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={pageTitle} />
                <meta name="twitter:description" content={seoDescription} />
                <meta name="twitter:site" content="@AntonetShop" />
                {productSchemaJson && (
                    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: productSchemaJson }} />
                )}
            </Head>

            <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
                <div className="lg:grid lg:grid-cols-12 lg:gap-x-10">

                    {/* Image gallery — left 7 cols */}
                    <div className="lg:col-span-7">
                        {/* Main image */}
                        <div className="overflow-hidden bg-white aspect-square flex items-center justify-center">
                            {imagesToShow.length > 0 ? (
                                <img
                                    src={imagesToShow[activeImage].imageSrc}
                                    alt={imagesToShow[activeImage].imageAlt}
                                    className="h-full w-full object-contain p-6"
                                />
                            ) : (
                                <div className="flex flex-col items-center gap-2 text-gray-400">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    <span className="text-sm">No image available</span>
                                </div>
                            )}
                        </div>
                        {/* Thumbnail strip */}
                        {imagesToShow?.length > 1 && (
                            <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
                                {imagesToShow.map((img, idx) => (
                                    <button
                                        key={img.id}
                                        onClick={() => setActiveImage(idx)}
                                        className={`shrink-0 overflow-hidden bg-gray-100 w-16 aspect-square transition-all ${
                                            activeImage === idx ? 'ring-2 ring-[#F93A00]' : 'ring-1 ring-transparent hover:ring-gray-300'
                                        }`}
                                    >
                                        <img src={img.imageSrc} alt={img.imageAlt} className="h-full w-full object-contain p-1" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Product info — right 5 cols */}
                    <div className="mt-8 lg:col-span-5 lg:mt-0">
                        <h1 className="text-xl font-bold uppercase tracking-wide text-[#222222]">
                            {product.name}
                        </h1>

                        {/* Price */}
                        {product.price && (
                            <div className="mt-3">
                                <p className="text-3xl font-black text-[#F93A00]">
                                    J${Number(product.price).toLocaleString('en-JM', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} JMD
                                </p>
                            </div>
                        )}

                        <form className="mt-6 space-y-6" onSubmit={(event) => {
                            event.preventDefault()
                            setMessage(null)
                            router.post(route('cart.store'), {
                                product_id: product.id,
                                color_id: selectedColor?.id,
                                color_name: selectedColor?.name,
                                size_id: selectedSize?.id,
                                size_name: selectedSize?.name,
                                variation_id: selectedVariation?.id,
                                quantity,
                            }, {
                                preserveScroll: true,
                                onSuccess: (page) => setMessage({ type: 'success', text: page.props.flash?.success || 'Added to your bag.' }),
                                onError: (errors) => setMessage({ type: 'error', text: Object.values(errors)[0] || 'Unable to add this item.' }),
                            })
                        }}>
                            {/* Color selector */}
                            {colors.length > 0 && (
                                <div>
                                    <h2 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">
                                        Color: <span className="text-gray-800">{selectedColor?.name}</span>
                                    </h2>
                                    <fieldset aria-label="Choose a color">
                                        <RadioGroup value={selectedColor} onChange={(color) => { setSelectedColor(color); setQuantity(1) }} className="flex items-center gap-2.5">
                                            {colors.map((color) => (
                                                <Radio
                                                    key={color.name}
                                                    value={color}
                                                    aria-label={color.name}
                                                    className={classNames(
                                                        color.selected_color,
                                                        'relative cursor-pointer flex items-center justify-center rounded-full p-0.5 focus:outline-none data-checked:ring-2 data-checked:ring-offset-1 data-checked:ring-[#F93A00]',
                                                    )}
                                                >
                                                    <span
                                                        aria-hidden="true"
                                                        className={classNames(color.bg_color, 'size-8 rounded-full border border-black/10')}
                                                    />
                                                </Radio>
                                            ))}
                                        </RadioGroup>
                                    </fieldset>
                                </div>
                            )}

                            {/* Size selector */}
                            {sizes.length > 0 && (
                                <div>
                                    <h2 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">
                                        Size: <span className="text-gray-800">{selectedSize?.name}</span>
                                    </h2>
                                    <fieldset aria-label="Choose a size">
                                        <RadioGroup
                                            value={selectedSize}
                                            onChange={(size) => { setSelectedSize(size); setQuantity(1) }}
                                            className="flex flex-wrap gap-2"
                                        >
                                            {sizes.map((size) => (
                                                <Radio
                                                    key={size.name}
                                                    value={size}
                                                    disabled={!size.is_active}
                                                    className={classNames(
                                                        size.is_active ? 'cursor-pointer' : 'cursor-not-allowed opacity-30',
                                                        'flex items-center justify-center border px-4 py-2 text-xs font-bold uppercase tracking-wide transition-colors focus:outline-none',
                                                        'data-checked:border-[#F93A00] data-checked:bg-[#F93A00] data-checked:text-white',
                                                        size.is_active ? 'border-gray-300 text-gray-700 hover:border-[#F93A00] hover:text-[#F93A00]' : 'border-gray-300 text-gray-700',
                                                    )}
                                                >
                                                    {size.name}
                                                </Radio>
                                            ))}
                                        </RadioGroup>
                                    </fieldset>
                                </div>
                            )}

                            {/* Add to cart */}
                            <div className="border-t border-gray-100 pt-5 text-sm text-gray-600">
                                <p><strong>SKU:</strong> {selectedVariation?.sku || product.sku || 'Available on request'}</p>
                                <p className={availableStock > 0 ? 'mt-1 text-green-700' : 'mt-1 text-red-600'}>{selectionReady ? (availableStock > 0 ? `${availableStock} available` : 'This variation is out of stock') : 'Select a size and colour to continue.'}</p>
                            </div>
                            {(sizes.length > 0 || colors.length > 0) && <div className="border-t border-gray-100 pt-5">
                                <h2 className="text-xs font-bold uppercase tracking-widest text-gray-500">Size chart</h2>
                                <p className="mt-2 text-sm text-gray-600">Measurements are approximate. Compare with a garment that fits you before ordering.</p>
                                <div className="mt-3 overflow-hidden rounded border border-gray-200">
                                    <table className="min-w-full text-left text-xs">
                                        <thead className="bg-gray-50 uppercase tracking-wide text-gray-500"><tr><th className="px-3 py-2">Size</th><th className="px-3 py-2">Waist (in)</th><th className="px-3 py-2">Availability</th></tr></thead>
                                        <tbody>{['S', 'M', 'L', 'XL'].map((size) => {
                                            const sizeOption = sizes.find((option) => option.name === size)
                                            const available = variations.filter((variation) => variation.size_id === sizeOption?.id).reduce((total, variation) => total + Number(variation.stock || 0), 0)
                                            return <tr key={size} className="border-t border-gray-100"><td className="px-3 py-2 font-semibold">{size}</td><td className="px-3 py-2">{size === 'S' ? '28–30' : size === 'M' ? '31–33' : size === 'L' ? '34–36' : '37–39'}</td><td className="px-3 py-2">{variations.length ? (available > 0 ? 'In stock' : 'Out of stock') : 'Select options'}</td></tr>
                                        })}</tbody>
                                    </table>
                                </div>
                            </div>}
                            {selectionReady && availableStock > 0 && <label className="block text-sm font-semibold text-gray-700">Quantity<select value={quantity} onChange={(event) => setQuantity(Number(event.target.value))} className="ml-3 border-gray-300 py-1 text-sm">{Array.from({ length: Math.min(availableStock, 10) }, (_, index) => <option key={index + 1} value={index + 1}>{index + 1}</option>)}</select></label>}
                            <button
                                type="submit"
                                disabled={!selectionReady || availableStock < 1}
                                className="flex w-full items-center justify-center gap-3 bg-[#222222] py-4 text-sm font-black uppercase tracking-widest text-white hover:bg-[#F93A00] transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                <ShoppingBagIcon className="size-5" />
                                {!selectionReady ? 'Select options' : availableStock < 1 ? 'Out of Stock' : 'Add to Bag'}
                            </button>
                            {message && <p role="status" className={message.type === 'error' ? 'text-sm text-red-600' : 'text-sm text-green-700'}>{message.text}</p>}
                        </form>


                        {/* Description */}
                        <div className="mt-6 border-t border-gray-100 pt-6">
                            <h2 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">Description</h2>
                            <div
                                dangerouslySetInnerHTML={{ __html: product.description }}
                                className="text-sm text-gray-600 leading-relaxed space-y-2"
                            />
                        </div>

                        {/* Details / Fabric & Care */}
                        {product.details?.length > 0 && (
                            <div className="mt-6 border-t border-gray-100 pt-6">
                                <h2 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">Fabric &amp; Care</h2>
                                <ul role="list" className="list-disc pl-4 space-y-1">
                                    {product.details.map((item) => (
                                        <li key={item} className="text-sm text-gray-600 pl-1">{item}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Share */}
                        <div className="mt-6 border-t border-gray-100 pt-6">
                            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">Share this product</p>
                            <div className="flex flex-wrap gap-2">
                                {/* Facebook */}
                                <a
                                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                                    style={{ backgroundColor: '#1877F2' }}
                                >
                                    <svg className="h-4 w-4 fill-current" viewBox="0 0 24 24"><path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.886v2.267h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/></svg>
                                    Facebook
                                </a>
                                {/* WhatsApp */}
                                <a
                                    href={`https://wa.me/?text=${encodeURIComponent(product.name + '\n' + window.location.href)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                                    style={{ backgroundColor: '#25D366' }}
                                >
                                    <svg className="h-4 w-4 fill-current" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                                    WhatsApp
                                </a>
                                {/* X / Twitter */}
                                <a
                                    href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent('Check out ' + product.name + ' on AntonetShop')}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-80"
                                >
                                    <svg className="h-4 w-4 fill-current" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.733-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                                    X
                                </a>
                                {/* Copy link */}
                                <button
                                    type="button"
                                    onClick={copyLink}
                                    className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
                                >
                                    {copied ? <CheckIcon className="h-4 w-4 text-green-500" /> : <LinkIcon className="h-4 w-4" />}
                                    {copied ? 'Copied!' : 'Copy link'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Reviews */}
                <div className="mt-16 border-t border-gray-200 pt-10">
                    <ReviewList reviews={reviews} />
                    {auth.user && <ReviewForm product={product} />}
                </div>

                {/* Related products */}
                {relatedProducts.length > 0 && (
                    <section className="mt-16 border-t border-gray-200 pt-10">
                        <h2 className="text-sm font-black uppercase tracking-widest text-[#222222] mb-6">
                            You May Also Like
                        </h2>
                        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-6">
                            {relatedProducts.map((related) => (
                                <a
                                    key={related.id}
                                    href={route('public.productDetail', related.slug || related.id)}
                                    className="group block"
                                >
                                    <div className="overflow-hidden bg-white aspect-square">
                                        <img
                                            src={related.image_url_first}
                                            alt={related.name}
                                            className="h-full w-full object-contain p-4 transition-transform duration-300 group-hover:scale-105"
                                        />
                                    </div>
                                    <p className="mt-2 text-xs text-gray-700 line-clamp-2 leading-tight">{related.name}</p>
                                </a>
                            ))}
                        </div>
                    </section>
                )}
            </main>
        </Theme>
    )
}
